import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import API from 'api/API';
import { QueryKeys } from 'api/models/QueryKeys';
import { QuickSwitchUser } from 'api/models/Users';
import { useCurrentUser } from './useCurrentUser';
import { useEnableUserQuickSwitcherForLocation } from './useLocationSetting';
import useEnableUserQuickSwitcher from 'hooks/useEnableUserQuickSwitcher';
import { clearCachedQuickSwitchUsers, setCachedQuickSwitchUsers } from 'hooks/useQuickSwitchUsersCache';
import { usePinOrgId } from 'hooks/usePinOrgId';
import { resolveOverrideOffSetting } from 'utils/helpers/locationSetting.helpers';

// fetches the users this device may quick-switch between; only succeeds once the location's
//  (or, for testing, this browser's) quick switcher setting is enabled
export const useQuickSwitchUsers = () => {
  const { data: user } = useCurrentUser();
  const { forLocation: quickSwitcherForLocation } = useEnableUserQuickSwitcherForLocation();
  const [quickSwitcherOnThisBrowser] = useEnableUserQuickSwitcher();
  const orgId = usePinOrgId();
  const orgName = user?.tenant?.name;

  const enabled = !!user && resolveOverrideOffSetting(quickSwitcherForLocation, quickSwitcherOnThisBrowser);

  const { data, isLoading } = useQuery<QuickSwitchUser[]>({
    queryKey: [QueryKeys.QuickSwitchUsers, user?.location?.locationId],
    queryFn: () => API.users.listQuickSwitchUsers(),
    enabled,
    retry: false,
    // a 400 here just means the setting is off for this location - not a real error to surface
    meta: { ignoreGlobalError: true },
  });

  // mirror the result to localStorage so the logged-out page can render it without a session
  useEffect(() => {
    if (data && orgId && orgName) setCachedQuickSwitchUsers(orgId, orgName, data);
  }, [data, orgId, orgName]);

  // once we know the setting is off, drop any previously cached emails
  useEffect(() => {
    if (user && !enabled && orgId) clearCachedQuickSwitchUsers(orgId);
  }, [user, enabled, orgId]);

  // the checkbox/toggle state is the source of truth, so don't show stale cached users once disabled
  return { quickSwitchUsers: enabled ? data ?? [] : [], isLoading: enabled && isLoading };
};
